/**
 * chatHistoryGrouping — pure presentation logic for the chat-history
 * timeline that sits above the live chat scroll.
 *
 * Extracted from NativeChatHistory.tsx so the three rules below are
 * testable without mounting React Native.
 *
 * ── Rule 1: weeks are Monday → Sunday, and must RENDER that way ──
 *
 * The data has always been Monday-anchored. `chat_history_weeks` carries
 * a CHECK constraint (migration 141):
 *
 *     CHECK (EXTRACT(DOW FROM week_start_date) = 1)  -- Monday
 *     CHECK (EXTRACT(DOW FROM week_end_date)   = 0)  -- Sunday
 *
 * so a Sunday-start row is structurally impossible. The trigger
 * (1354_chat_history_message_link_trigger.sql) and
 * chat_history_processor.get_current_week_boundaries agree.
 *
 * Yet the UI showed every week as Sunday→Saturday. The cause was the
 * renderer, not the data: the old formatter called
 * `new Date("2026-08-10")`, and a bare date-only string is parsed as
 * UTC midnight. `.getDate()` then reads it back in device-local time,
 * so everyone west of UTC — all of the US and Canada — saw the
 * PREVIOUS day. Monday Aug 10 → Sunday Aug 16 rendered as "Aug 9-15".
 *
 * This is the repo's known local-date trap (see utils/sharedFormatters
 * `parseLocalDate`). `formatWeekRange` below is timezone-free by
 * construction: it reads the Y/M/D fields out of the string and never
 * builds a Date from a bare date literal, so it cannot drift again.
 *
 * ── Rule 2: chronological order, oldest at top ──
 *
 * This list is the header of a chat scroll, where content flows
 * oldest → newest downward and the live conversation sits at the
 * bottom. Newest-first ordering made the list run backwards against
 * that flow and then jump forward in time at the "This Week" divider
 * (April sat directly above an August "This Week"). Buckets and the
 * weeks inside them are ascending, so the newest past week lands
 * immediately above "This Week" and time reads continuously downward.
 *
 * ── Rule 3: the current week appears exactly once ──
 *
 * The backend's month rollup includes the in-progress week, and it is
 * also returned separately as `currentWeek`. Rendering both listed the
 * same week twice — once under "Previous Weeks" and again under "This
 * Week". Reading it on a Sunday (the last day of a Monday-anchored
 * week) made a still-open week look like it had already been filed
 * away. `buildMonthBuckets` removes it from the past-week set.
 */

import type { MonthGroup, WeekGroup } from '../types/ChatHistory';

// Locale- and timezone-independent by design. `toLocaleDateString`
// pulls in the device locale and, for date-only inputs, the UTC parse
// that caused the off-by-one in the first place.
const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** Label for the bucket holding weeks whose start date is unusable. */
export const UNDATED_BUCKET_LABEL = 'EARLIER';

interface YMD {
  year: number;
  month: number; // 0-11
  day: number;   // 1-31
}

/**
 * Pull Y/M/D out of an ISO date or datetime string WITHOUT constructing
 * a Date. Accepts "2026-08-10" and "2026-08-10T00:00:00Z" alike.
 * Returns null for anything that isn't a leading YYYY-MM-DD.
 */
export function readYMD(value: string | null | undefined): YMD | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month: month - 1, day };
}

/**
 * Render a week's date range for display.
 *
 * Same-month weeks collapse to "Aug 10-16"; weeks that straddle a month
 * boundary render both months, "Jul 27 - Aug 2".
 *
 * Returns '' when either endpoint is unusable — callers render nothing
 * rather than a half-formed range.
 */
export function formatWeekRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  const start = readYMD(startDate);
  const end = readYMD(endDate);
  if (!start || !end) return '';

  const startMonth = SHORT_MONTHS[start.month];
  const endMonth = SHORT_MONTHS[end.month];

  if (start.month === end.month && start.year === end.year) {
    return `${startMonth} ${start.day}-${end.day}`;
  }
  return `${startMonth} ${start.day} - ${endMonth} ${end.day}`;
}

/** "AUGUST 2026" — the month-bucket header. */
export function formatMonthLabel(year: number, month: number): string {
  const name = LONG_MONTHS[month] ?? '';
  return `${name} ${year}`.trim().toUpperCase();
}

/** A month's worth of past weeks, ascending. */
export interface MonthBucket {
  /** Stable key for React lists. */
  id: string;
  /** "AUGUST 2026", or UNDATED_BUCKET_LABEL for unusable start dates. */
  label: string;
  /** Year, or null for the undated bucket. */
  year: number | null;
  /** 0-11, or null for the undated bucket. */
  month: number | null;
  /** Ascending by start date — oldest first. */
  weeks: WeekGroup[];
}

/** A week's identity for dedupe/comparison. Prefers id, falls back to date. */
function identityOf(week: Pick<WeekGroup, 'id' | 'startDate'>): {
  id: string;
  day: string | null;
} {
  const ymd = readYMD(week.startDate);
  return {
    id: (week.id ?? '').trim(),
    // Zero-pad so string comparison is safe.
    day: ymd
      ? `${ymd.year}-${String(ymd.month + 1).padStart(2, '0')}-${String(ymd.day).padStart(2, '0')}`
      : null,
  };
}

/**
 * True when `week` is the same week as `currentWeek`.
 *
 * Matches on id when both carry one — but the backend's CH-H2 fallback
 * path emits `weekId: ""` when the week row is missing, so start date is
 * the second, equally authoritative key (weeks are UNIQUE per
 * (user_id, week_start_date)).
 */
function isSameWeek(
  week: Pick<WeekGroup, 'id' | 'startDate'>,
  currentWeek: Pick<WeekGroup, 'id' | 'startDate'>,
): boolean {
  const a = identityOf(week);
  const b = identityOf(currentWeek);
  if (a.id && b.id && a.id === b.id) return true;
  if (a.day && b.day && a.day === b.day) return true;
  return false;
}

/**
 * Flatten the backend's month rollup into month buckets for display.
 *
 * - drops the in-progress week (it renders under "This Week")
 * - de-duplicates weeks that appear under more than one month
 * - sorts weeks and buckets ascending (oldest first)
 * - buckets by the month of the week's START date, matching the
 *   backend's own rollup key (`EXTRACT(MONTH FROM week_start_date)`),
 *   so a week straddling a month boundary lands in one bucket only
 *
 * Weeks whose start date can't be read are kept — never silently
 * dropped — in a leading UNDATED_BUCKET_LABEL bucket.
 */
export function buildMonthBuckets(
  months: MonthGroup[] | null | undefined,
  currentWeek?: Pick<WeekGroup, 'id' | 'startDate'> | null,
): MonthBucket[] {
  const flat: WeekGroup[] = (months ?? []).flatMap((m) => m?.weeks ?? []);

  const seenIds = new Set<string>();
  const seenDays = new Set<string>();
  const kept: WeekGroup[] = [];

  for (const week of flat) {
    if (!week) continue;
    if (currentWeek && isSameWeek(week, currentWeek)) continue;

    const { id, day } = identityOf(week);
    if (id && seenIds.has(id)) continue;
    if (day && seenDays.has(day)) continue;
    if (id) seenIds.add(id);
    if (day) seenDays.add(day);

    kept.push(week);
  }

  const undated: WeekGroup[] = [];
  const byMonth = new Map<string, MonthBucket>();

  for (const week of kept) {
    const ymd = readYMD(week.startDate);
    if (!ymd) {
      undated.push(week);
      continue;
    }
    const key = `${ymd.year}-${String(ymd.month + 1).padStart(2, '0')}`;
    let bucket = byMonth.get(key);
    if (!bucket) {
      bucket = {
        id: `chat-month-${key}`,
        label: formatMonthLabel(ymd.year, ymd.month),
        year: ymd.year,
        month: ymd.month,
        weeks: [],
      };
      byMonth.set(key, bucket);
    }
    bucket.weeks.push(week);
  }

  // Ascending within each bucket — oldest week at the top.
  for (const bucket of byMonth.values()) {
    bucket.weeks.sort((a, b) => {
      const aDay = identityOf(a).day ?? '';
      const bDay = identityOf(b).day ?? '';
      return aDay < bDay ? -1 : aDay > bDay ? 1 : 0;
    });
  }

  // Ascending across buckets, oldest month first.
  const dated = Array.from(byMonth.values()).sort((a, b) => {
    if (a.year !== b.year) return (a.year ?? 0) - (b.year ?? 0);
    return (a.month ?? 0) - (b.month ?? 0);
  });

  if (undated.length === 0) return dated;

  return [
    {
      id: 'chat-month-undated',
      label: UNDATED_BUCKET_LABEL,
      year: null,
      month: null,
      weeks: undated,
    },
    ...dated,
  ];
}

/** Total weeks across buckets — the count in "Previous Weeks (N)". */
export function countWeeks(buckets: MonthBucket[]): number {
  return buckets.reduce((sum, b) => sum + b.weeks.length, 0);
}

/**
 * Which bucket should start expanded.
 *
 * The last one — the most recent month, sitting directly above "This
 * Week". It is both the nearest in time and the closest to the live
 * conversation, so it is what a reader scrolling up wants first.
 * Returns null when there is nothing to expand.
 */
export function defaultExpandedBucketId(buckets: MonthBucket[]): string | null {
  if (buckets.length === 0) return null;
  return buckets[buckets.length - 1].id;
}
